import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/api/client";
import { toast } from "sonner";
import TwoFactorSetup from "@/components/auth/TwoFactorSetup";
import TwoFactorChallenge from "@/components/auth/TwoFactorChallenge";

export default function Login() {
  const { beginLogin, completeLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // 2FA-tila: { stage: 'setup' | 'challenge', token, email, full_name }
  const [twofa, setTwofa] = useState(null);

  const dest = location.state?.from?.pathname || "/";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await beginLogin(email, password);
      if (res?.twofa_required) {
        if (res.twofa_setup) {
          setTwofa({
            stage: "setup",
            token: res.setup_token,
            email: res.email,
            full_name: res.full_name,
          });
        } else {
          setTwofa({
            stage: "challenge",
            token: res.challenge_token,
            email: res.email,
          });
        }
      } else {
        // Backward fallback – ei pitäisi tapahtua koska 2FA on pakollinen
        completeLogin(res);
        navigate(dest, { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleted = (userPayload) => {
    completeLogin(userPayload);
    toast.success("Tervetuloa! 2FA aktiivinen.");
    navigate(dest, { replace: true });
  };

  const cancelTwofa = () => {
    setTwofa(null);
    setPassword("");
  };

  if (twofa?.stage === "setup") {
    return (
      <TwoFactorSetup
        setupToken={twofa.token}
        email={twofa.email}
        fullName={twofa.full_name}
        onCompleted={handleCompleted}
        onCancel={cancelTwofa}
      />
    );
  }

  if (twofa?.stage === "challenge") {
    return (
      <TwoFactorChallenge
        challengeToken={twofa.token}
        email={twofa.email}
        onCompleted={handleCompleted}
        onCancel={cancelTwofa}
        onSelfReset={(payload) => {
          // Hyppää suoraan SETUP-flowhin uudella setup-tokenilla
          setTwofa({
            stage: "setup",
            token: payload.setup_token,
            email: payload.email,
            full_name: payload.full_name,
          });
        }}
      />
    );
  }

  return (
    <div className="app-bg flex items-center justify-center px-4 py-12 min-h-screen relative">
      <div className="w-full max-w-md glass-strong p-8 relative z-10 animate-fade-in" data-testid="login-page">
        <div className="text-center mb-8">
          <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 via-amber-500 to-orange-600 flex items-center justify-center mx-auto mb-4">
            <span className="text-zinc-900 font-display font-extrabold text-xl tracking-tight">A2G</span>
            <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-amber-400 to-purple-600 opacity-30 blur-md -z-10" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Apps2Go Hub</p>
          <h1 className="font-display text-3xl gradient-text-luxe mt-1">Tervetuloa</h1>
          <p className="text-xs text-white/50 mt-2">Kirjaudu jatkaaksesi sovellukseen</p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-white/60">Sähköposti</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nimi@yritys.fi"
              required
              data-testid="login-email"
              className="bg-black/30 border-white/10 h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-white/60">Salasana</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
              className="bg-black/30 border-white/10 h-11"
            />
          </div>

          {error && (
            <div className="text-sm text-red-200 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="btn-luxe w-full h-11 inline-flex items-center justify-center"
          >
            {submitting ? "Kirjaudutaan..." : "Kirjaudu sisään"}
          </Button>
        </form>

        <div className="mt-6 text-center text-xs text-white/40 border-t border-white/10 pt-4 space-y-1">
          <p>Tilit luo vain pääkäyttäjä. Pyydä tunnukset ylläpidolta.</p>
          <p className="text-amber-200/70">2FA pakollinen kaikille käyttäjille – aktivoidaan ensimmäisellä kirjautumisella.</p>
        </div>
      </div>
    </div>
  );
}
